// ============================================================================
// Hire refund eligibility — brand wallet credit
// ============================================================================
// A hire that blows its deadline without delivering gets refunded to the
// brand's WALLET as credit (never back to the card). This module decides
// whether a hire qualifies and how much the default refund should be.
//
// It is a MIRROR of the rules in wallet_refund_hire_to_credit() (defined in
// creator-app/supabase/migrations/20260804020000_brand_wallet_v2.sql; rule (d)
// last amended by 20260806000000_wallet_refund_respects_grace_period.sql). The
// database is the authority — it re-checks every rule and rejects a refund that
// doesn't qualify no matter what the UI thought. This exists so ops sees the
// button (and the reason it's disabled) without a round-trip, not to gate the
// money. If the two ever disagree, the DB wins and the fix goes here.
//
// ONE rule here is deliberately STRICTER than the database: remainingOwedCents.
// The DB caps a refund at collected - refunded (rule (e)), which is the right
// ceiling for the money — it still permits a goodwill refund that covers videos
// the brand actually received, and ops can make one as a Full refund. But it says
// nothing about whether anything is still OWED, so on its own it left the Refund
// button live on hires whose undelivered videos had already been refunded in
// full. Offering less than the DB permits is safe; the reverse would not be.
// ============================================================================

import { isWithinGracePeriod, gracePeriodEndsAt, graceHoursRemaining } from './deadlines'

export interface RefundableHire {
  status: string
  deadline_at: string | null
  videos_required: number
  /** Credit committed to this hire — 0 for card-only hires. */
  credit_applied_cents: number | null
  /** Collected from the card — 0 for wallet-only hires. */
  card_charged_cents: number | null
  refunded_credit_cents: number | null
  whop_charge_status: string | null
  // ── Deadline extension, for the pending-request block below ───────────────
  // Optional so the many callers that never had them (and the tests) keep
  // working: absent reads as "no request", which is the common case.
  deadline_extension_requested_at?: string | null
  deadline_extended_at?: string | null
  deadline_extension_rejected_at?: string | null
}

/**
 * The creator asked for more time and nobody has granted or rejected it yet.
 *
 * Same three-field test as hire-queue's extensionPending() and the worker's
 * cronAutoRefundZeroDelivery — a request is "answered" once EITHER
 * deadline_extended_at or deadline_extension_rejected_at is set, and a hire that
 * has been through this before carries the old timestamps, so checking
 * requested_at alone would block forever after the first request.
 */
export function extensionRequestPending(hire: RefundableHire): boolean {
  return !!hire.deadline_extension_requested_at
    && !hire.deadline_extended_at
    && !hire.deadline_extension_rejected_at
}

export interface RefundState {
  /** What the brand actually paid: wallet credit + card. */
  collectedCents: number
  /** Already refunded to the wallet for this hire. */
  refundedCents: number
  /**
   * Money-level ceiling: collected - refunded. What the DB will accept at most
   * (wallet_refund_hire_to_credit rule (e)). NOT the same as "what a refund is
   * owed" — see remainingOwedCents, which is what the UI acts on.
   */
  refundableCents: number
  /**
   * What the brand is owed in total for videos that never arrived:
   * per-video price x undelivered. The entitlement a refund draws down.
   */
  undeliveredOwedCents: number
  /**
   * Entitlement still unsettled: undeliveredOwed - already refunded, floored at
   * 0 and capped by refundableCents. THIS is what the Refund button means, and
   * the default amount when one is offered.
   *
   * Distinct from refundableCents on purpose. A hire that collected $867 for 10
   * videos, received 2 and was refunded the other 8 ($693.60) still has $173.42
   * of collected money left — but that money is the price of the 2 videos the
   * brand DID receive and the creator has been paid for. It is not refundable in
   * any meaningful sense, and treating it as such is what made the Refund button
   * reappear on fully-settled hires.
   */
  remainingOwedCents: number
  deliveredVideos: number
  undeliveredVideos: number
  /**
   * Undelivered videos not yet refunded — remainingOwed expressed in videos.
   * Caps the "videos to refund" input, which previously offered every
   * undelivered video again on every subsequent refund.
   */
  refundableVideos: number
  /** What one video of this package cost the brand. Drives partial refunds. */
  perVideoCents: number
  eligible: boolean
  /** Why the refund button is unavailable. null when eligible. */
  blockedReason: string | null
  /**
   * Deadline has passed but the creator's 48h grace window is still open, so the
   * refund is blocked *temporarily* — it becomes available on its own once the
   * window closes. Distinct from the other blockers, which need someone to act
   * (revert a completion, wait for a charge to settle) or never clear at all.
   */
  inGracePeriod: boolean
  /** When the grace window closes. Only meaningful while inGracePeriod. */
  graceEndsAt: Date | null
  fullyRefunded: boolean
  partiallyRefunded: boolean
  /**
   * True when a partial refund is meaningless — the creator delivered nothing,
   * so there is no video to keep paying for. The UI offers full refund only.
   */
  fullOnly: boolean
}

/**
 * @param deliveredVideos videos that count toward the requirement — submitted,
 *        not admin-rejected, not since deleted from TikTok. Same definition the
 *        payments table uses for its "Videos" column and the DB uses in
 *        hire_delivered_video_count().
 */
export function refundState(hire: RefundableHire, deliveredVideos: number): RefundState {
  const collectedCents = (hire.credit_applied_cents ?? 0) + (hire.card_charged_cents ?? 0)
  const refundedCents  = hire.refunded_credit_cents ?? 0
  const refundableCents = Math.max(0, collectedCents - refundedCents)
  const required = hire.videos_required ?? 0
  const undeliveredVideos = Math.max(0, required - deliveredVideos)

  // Per-video price from what was actually COLLECTED, not the hire's list
  // price: a hire charged an adjusted amount must not price its videos off a
  // number the brand never paid.
  const perVideoCents = required > 0 ? Math.round(collectedCents / required) : collectedCents

  // The brand's entitlement: what the undelivered videos cost them.
  //
  // Deliberately perVideoCents x undelivered — the SAME arithmetic
  // partialRefundCents() uses — rather than the mathematically tidier
  // round(collected x undelivered / required). The two differ by rounding, and
  // that difference is not cosmetic: on a $867.02 / 10-video hire, per-video
  // rounds to $86.70 so refunding all 8 undelivered videos credits $693.60,
  // while the tidier formula calls the entitlement $693.62. Two cents would be
  // left "owed" forever, and the Refund button would never switch off.
  // Entitlement and refund amount must be computed identically or a settled hire
  // never reads as settled.
  const undeliveredOwedCents = required > 0
    ? Math.min(collectedCents, perVideoCents * undeliveredVideos)
    : refundableCents

  // Everything already refunded counts against the entitlement, including a
  // goodwill full refund that covered delivered videos too — that hire is
  // over-settled, not under-settled, so it clamps to 0 rather than going negative.
  const remainingOwedCents = Math.max(0, Math.min(refundableCents, undeliveredOwedCents - refundedCents))
  const refundableVideos = perVideoCents > 0
    ? Math.min(undeliveredVideos, Math.round(remainingOwedCents / perVideoCents))
    : 0

  const fullyRefunded = collectedCents > 0 && refundedCents >= collectedCents
  const partiallyRefunded = refundedCents > 0 && !fullyRefunded
  const inGracePeriod = isWithinGracePeriod(hire.deadline_at)

  const blockedReason = ((): string | null => {
    if (collectedCents <= 0) {
      return hire.whop_charge_status
        ? `No payment collected (charge: ${hire.whop_charge_status})`
        : 'No payment was collected for this hire'
    }
    if (hire.whop_charge_status === 'pending') {
      return 'Charge is still settling — wait for it to resolve'
    }
    if (hire.status === 'completed') {
      return 'Hire is completed — revert it before refunding'
    }
    if (deliveredVideos >= required && required > 0) {
      return `All ${required} videos delivered`
    }
    if (fullyRefunded) {
      return 'Already fully refunded'
    }
    if (refundableCents <= 0) {
      return 'Nothing left to refund'
    }
    // Settled: every undelivered video has been refunded. Money remains on the
    // hire, but it belongs to the videos the brand actually received — the
    // creator is paid (or payable) for exactly those. Without this the button
    // came back after every partial refund offering the delivered videos' value,
    // which is how a hire that was fully settled still looked refundable.
    if (remainingOwedCents <= 0) {
      return `The ${undeliveredVideos} undelivered video${undeliveredVideos === 1 ? '' : 's'} on this hire ${undeliveredVideos === 1 ? 'has' : 'have'} already been refunded (${usd(refundedCents)}) — the ${usd(refundableCents)} still on the hire is the price of the ${deliveredVideos} video${deliveredVideos === 1 ? '' : 's'} the creator delivered`
    }
    if (!hire.deadline_at) {
      return 'No deadline set on this hire'
    }
    const daysLeft = Math.ceil((new Date(hire.deadline_at).getTime() - Date.now()) / 86400000)
    if (daysLeft > 0) {
      return `Deadline hasn't passed (${daysLeft}d left)`
    }
    // The creator keeps 48h past the deadline to submit or request an extension.
    // Refunding inside it could hand the brand back money for videos that then
    // land, or defund a hire that then gets extended — so the DB rejects it and
    // ops shouldn't see the button yet.
    if (inGracePeriod) {
      const hours = graceHoursRemaining(hire.deadline_at)
      return `Grace period — ${hours}h left for the creator to submit or request an extension`
    }

    // The creator asked for more time and nobody has answered. Refunding now is
    // what the grace window exists to prevent, one step later: give the money
    // back, then grant the extension, and the hire is defunded while the creator
    // is still working — the brand has its money, the creator keeps delivering,
    // and nothing pays them at the end.
    //
    // Deliberately LAST. Every check above describes a hire that could never be
    // refunded anyway (nothing collected, already refunded, all videos in) or a
    // clock that hasn't run out — each is a more useful thing to tell ops than
    // "answer the extension". This only fires on a hire that is otherwise ready
    // to refund, which is exactly when the pending request is the one thing in
    // the way.
    //
    // There is no override, and none is needed: the escape hatch is to answer the
    // request. Rejecting a stale one takes a click and is the correct action
    // anyway — a request nobody intends to grant should not sit open.
    //
    // cronAutoRefundZeroDelivery already skips these hires for the same reason.
    // This closes the manual path, which was the half still open.
    if (extensionRequestPending(hire)) {
      return 'The creator asked for a deadline extension — grant or reject it first, then refund'
    }

    return null
  })()

  return {
    inGracePeriod,
    graceEndsAt: gracePeriodEndsAt(hire.deadline_at),
    collectedCents,
    refundedCents,
    refundableCents,
    undeliveredOwedCents,
    remainingOwedCents,
    deliveredVideos,
    undeliveredVideos,
    refundableVideos,
    perVideoCents,
    eligible: blockedReason === null,
    blockedReason,
    fullyRefunded,
    partiallyRefunded,
    // Nothing delivered → there's nothing to keep paying for, so a partial
    // refund makes no sense and the UI shouldn't offer it.
    fullOnly: deliveredVideos === 0,
  }
}

/**
 * What a partial refund costs when the brand is refunded for `videosToRefund`
 * of the package and keeps paying for the rest.
 *
 * Bounded by refundableVideos, not undeliveredVideos: on a hire that has already
 * been refunded for 3 of its 8 undelivered videos, only 5 remain to refund, and
 * offering 8 would re-refund work already settled. Also capped in cents at the
 * unsettled entitlement so rounding can't push it past it.
 */
export function partialRefundCents(state: RefundState, videosToRefund: number): number {
  const n = Math.max(0, Math.min(videosToRefund, state.refundableVideos))
  return Math.min(state.remainingOwedCents, state.perVideoCents * n)
}

/**
 * Who performed a refund, rendered for ops.
 *
 * Refunds are attributable by contract — wallet_refund_hire_to_credit rejects an
 * empty p_created_by — and automated callers pass a `system:<what>` actor instead
 * of an admin email. That prefix IS the marker for "this was not a person":
 * it needs no extra column, it is already the convention across the worker and the
 * migrations ('system:deadline_cron', 'system:backfill', 'system:hire_charge',
 * 'system:hire_release', 'system:refund'), and it stays queryable —
 * `refunded_by LIKE 'system:%'` finds every automatic refund.
 *
 * Worth surfacing rather than leaving as a raw string: ops looking at money that
 * moved on its own needs to know nobody decided it, and "system:deadline_cron" in
 * a sentence that reads "credited by ..." looks like a broken record.
 */
const SYSTEM_ACTOR_LABELS: Record<string, string> = {
  'system:deadline_cron': 'Automatic — deadline sweep',
  'system:backfill':      'Automatic — data backfill',
  'system:refund':        'Automatic — refund',
}

/** True when a refund was made by an automated path, not a person. */
export function isSystemActor(actor: string | null | undefined): boolean {
  return !!actor && actor.startsWith('system:')
}

/** Display name for a refund actor: the admin's email, or what automated it. */
export function refundActorLabel(actor: string | null | undefined): string | null {
  if (!actor) return null
  if (!isSystemActor(actor)) return actor
  return SYSTEM_ACTOR_LABELS[actor] ?? 'Automatic — system'
}

/** "$100 credit + $50.30 card" — how a hire was funded, for the payments UI. */
export function fundingLabel(hire: Pick<RefundableHire, 'credit_applied_cents' | 'card_charged_cents'>): string | null {
  const credit = hire.credit_applied_cents ?? 0
  const card   = hire.card_charged_cents ?? 0
  if (credit > 0 && card > 0) return `${usd(credit)} credit + ${usd(card)} card`
  if (credit > 0) return `${usd(credit)} credit`
  if (card > 0)   return `${usd(card)} card`
  return null
}

/**
 * Money from cents. Shows cents only when there are cents, and then ALWAYS two
 * digits — a naive `maximumFractionDigits: 2` renders 4730 as "$47.3", which
 * reads like a truncated figure on a refund the brand will reconcile.
 * Use this for every dollar amount in the refund UI.
 */
export function usd(cents: number): string {
  const whole = Math.abs(cents) % 100 === 0
  const digits = whole ? 0 : 2
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`
}
